import { stableValueHash } from '../hash/stable-value-hash';
import { isPrimitive } from '../primitive/primitive';
import type { Func } from '../types/function';

interface MemoizeOptions {
  maxSize?: number;
}

export function memoize<T extends Func>(fn: T, options?: MemoizeOptions): T {
  const maxSize = options?.maxSize ?? 100;
  const cache: Map<string, ReturnType<T>> = new Map();

  return ((...args: Parameters<T>): ReturnType<T> => {
    const key = args.length === 1 && isPrimitive(args[0]) ? String(args[0]) : stableValueHash(args);

    let result = cache.get(key);
    if (result !== undefined || cache.has(key)) {
      // Delete and re-set to update position in Map for LRU eviction
      cache.delete(key);
      cache.set(key, result!);

      return result!;
    }

    result = fn(...args);
    cache.set(key, result!);

    if (cache.size > maxSize) {
      const oldestKey = cache.keys().next().value;
      if (oldestKey !== undefined) {
        cache.delete(oldestKey);
      }
    }

    return result!;
  }) as T;
}
